import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { LibraryItemPicker } from '../../components/studio/LibraryItemPicker';
import { useToast } from '../../components/ui/toast';
import { describeApiError } from '../../data/http';
import {
  addReferenceItem,
  createReferenceEntry,
  getReferenceEntry,
  referenceItemContentUrl,
  removeReferenceItem,
  updateReferenceEntry
} from '../../data/reference';
import { readFileAsBase64 } from '../../data/templates';
import type {
  FileCategory,
  LibraryItem,
  ReferenceEntryItemSummary,
  ReferenceKind
} from '../../data/types';

const KINDS: ReferenceKind[] = ['text', 'file', 'table', 'imageCollection'];

const inputClass =
  'mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

export function ReferenceEntryPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { entryId } = useParams();
  const [search] = useSearchParams();

  const isNew = entryId === 'new';
  const scope = search.get('scope') ?? 'global';

  const [key, setKey] = useState('');
  const [kind, setKind] = useState<ReferenceKind>('text');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [currentFileName, setCurrentFileName] = useState('');
  const [fileSource, setFileSource] = useState<'upload' | 'library'>('upload');
  const [fileLibraryItem, setFileLibraryItem] = useState<LibraryItem | null>(null);
  const [fileCategory, setFileCategory] = useState<FileCategory>('');
  const [fileExtension, setFileExtension] = useState('');
  const [items, setItems] = useState<ReferenceEntryItemSummary[]>([]);

  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (isNew || !entryId) return Promise.resolve();
    return getReferenceEntry(scope, entryId)
      .then((detail) => {
        setKey(detail.entry.key);
        setKind(detail.entry.kind);
        setTitle(detail.entry.title);
        setDescription(detail.entry.description);
        setValue(detail.entry.value);
        setCurrentFileName(detail.entry.fileName);
        setFileCategory(detail.entry.fileCategory);
        setFileExtension(detail.entry.fileExtension);
        setItems(detail.items);
        // A file/table entry can be SharePoint-sourced too — without this, re-opening one would
        // default back to the upload tab and saving unchanged would look like a missing file.
        if ((detail.entry.kind === 'file' || detail.entry.kind === 'table') && detail.entry.libraryItemId) {
          setFileSource('library');
          setFileLibraryItem({
            id: detail.entry.libraryItemId,
            name: detail.entry.fileName,
            fileName: detail.entry.fileName,
            modified: '',
            isFolder: false
          });
        } else {
          setFileSource('upload');
          setFileLibraryItem(null);
        }
        setError(null);
      })
      .catch((err: unknown) => setError(describeApiError(err, t('errors.loadReference'))))
      .finally(() => setLoading(false));
  }, [entryId, isNew, scope, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (key.trim().length === 0) {
      toast({ title: t('studio.referenceEntry.keyRequired'), type: 'error' });
      return;
    }

    if (kind === 'file' || kind === 'table') {
      if (fileSource === 'upload' && !file && !currentFileName) {
        toast({ title: t('studio.referenceEntry.fileRequired'), type: 'error' });
        return;
      }
      if (fileSource === 'library' && !fileLibraryItem) {
        toast({ title: t('studio.referenceEntry.libraryItemRequired'), type: 'error' });
        return;
      }
    }

    setBusy(true);
    try {
      const usingFileLibrary = (kind === 'file' || kind === 'table') && fileSource === 'library';
      const fileContentBase64 = file && !usingFileLibrary ? await readFileAsBase64(file) : undefined;

      const payload = {
        kind,
        title,
        description,
        value: kind === 'text' ? value : undefined,
        fileName: usingFileLibrary ? fileLibraryItem?.fileName : file?.name,
        fileContentBase64,
        libraryItemId: usingFileLibrary ? fileLibraryItem?.id : undefined
      };

      if (isNew) {
        await createReferenceEntry({ key: key.trim(), scope, ...payload });
      } else {
        await updateReferenceEntry(scope, key.trim(), payload);
      }

      toast({ title: t('studio.referenceEntry.saved'), type: 'success' });

      if (isNew) {
        navigate(`/studio/reference/${encodeURIComponent(key.trim())}?scope=${encodeURIComponent(scope)}`);
      } else {
        await load();
        setFile(null);
      }
    } catch (err: unknown) {
      toast({ title: describeApiError(err, t('errors.saveReference')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const addImage = async (chosen: File | null) => {
    if (!chosen || !entryId) return;
    setBusy(true);
    try {
      const detail = await addReferenceItem(scope, entryId, {
        fileName: chosen.name,
        fileContentBase64: await readFileAsBase64(chosen)
      });
      setItems(detail.items);
    } catch (err: unknown) {
      toast({ title: describeApiError(err, t('errors.saveItem')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const removeImage = async (item: ReferenceEntryItemSummary) => {
    if (!entryId) return;
    setBusy(true);
    try {
      await removeReferenceItem(scope, entryId, item.id);
      setItems((current) => current.filter((i) => i.id !== item.id));
    } catch (err: unknown) {
      toast({ title: describeApiError(err, t('errors.saveItem')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (error) return <p className="text-sm text-destructive">{error}</p>;

  const detectedType = fileExtension
    ? fileExtension.toUpperCase()
    : fileCategory && kind === 'file'
      ? t(`reference.categories.${fileCategory}`)
      : null;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <Link
          to="/studio/reference"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeftIcon className="size-4" aria-hidden="true" />
          {t('studio.referenceEntry.back')}
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
          {isNew ? t('studio.referenceEntry.titleNew') : t('studio.referenceEntry.titleEdit')}
        </h1>
      </div>

      <div className="space-y-4 rounded-md border border-border bg-surface p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="entry-key" className="text-sm font-medium text-foreground">
              {t('studio.referenceEntry.key')}
            </label>
            <input
              id="entry-key"
              type="text"
              className={inputClass}
              value={key}
              disabled={!isNew}
              onChange={(event) => setKey(event.target.value)}
            />
            {isNew && <p className="mt-1 text-xs text-muted-foreground">{t('studio.referenceEntry.keyHint')}</p>}
          </div>
          <div>
            <label htmlFor="entry-kind" className="text-sm font-medium text-foreground">
              {t('studio.referenceEntry.kind')}
            </label>
            <select
              id="entry-kind"
              className={inputClass}
              value={kind}
              disabled={!isNew}
              onChange={(event) => setKind(event.target.value as ReferenceKind)}
            >
              {KINDS.map((option) => (
                <option key={option} value={option}>
                  {t(`reference.kinds.${option}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="entry-title" className="text-sm font-medium text-foreground">
            {t('studio.referenceEntry.entryTitle')}
          </label>
          <input id="entry-title" type="text" className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div>
          <label htmlFor="entry-description" className="text-sm font-medium text-foreground">
            {t('studio.referenceEntry.description')}
          </label>
          <input
            id="entry-description"
            type="text"
            className={inputClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {kind === 'text' && (
          <div>
            <label htmlFor="entry-value" className="text-sm font-medium text-foreground">
              {t('studio.referenceEntry.value')}
            </label>
            <textarea id="entry-value" rows={3} className={inputClass} value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
        )}

        {(kind === 'file' || kind === 'table') && (
          <div className="space-y-3">
            <fieldset>
              <legend className="text-sm font-medium text-foreground">{t('studio.upload.source')}</legend>
              <div className="mt-1 flex flex-wrap gap-4">
                {(['upload', 'library'] as const).map((option) => (
                  <label key={option} className="inline-flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="entry-file-source"
                      value={option}
                      checked={fileSource === option}
                      onChange={() => setFileSource(option)}
                      className="size-4 accent-primary"
                    />
                    <span className="text-foreground">{t(`studio.upload.source_${option}`)}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {fileSource === 'upload' ? (
              <div>
                <label htmlFor="entry-file" className="text-sm font-medium text-foreground">
                  {currentFileName ? t('studio.referenceEntry.replaceFile') : t('studio.referenceEntry.file')}
                </label>
                <input
                  id="entry-file"
                  type="file"
                  className={inputClass}
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
                {currentFileName && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('studio.referenceEntry.currentFile', { name: currentFileName })}
                    {detectedType ? ` — ${t('reference.detectedType')}: ${detectedType}` : ''}
                  </p>
                )}
              </div>
            ) : (
              <LibraryItemPicker
                idPrefix="entry-file"
                value={fileLibraryItem?.id ?? ''}
                onChange={setFileLibraryItem}
                libraryType="Blocks"
              />
            )}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={() => void save()}
            className="inline-flex items-center rounded-sm bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t('studio.referenceEntry.saving') : t('studio.referenceEntry.save')}
          </button>
        </div>
      </div>

      {kind === 'imageCollection' && (
        <section className="rounded-md border border-border bg-surface p-5">
          <h2 className="text-base font-semibold text-foreground">{t('studio.referenceEntry.items')}</h2>

          {isNew ? (
            <p className="mt-2 text-sm text-muted-foreground">{t('studio.referenceEntry.itemsAfterSave')}</p>
          ) : (
            <>
              {items.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">{t('studio.referenceEntry.noItems')}</p>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {items.map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-3 py-2">
                      <a
                        href={referenceItemContentUrl(scope, key, item.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate text-sm text-foreground underline-offset-2 hover:underline"
                      >
                        {item.fileName}
                      </a>
                      <button
                        type="button"
                        disabled={busy}
                        aria-label={t('studio.referenceEntry.removeItem', { name: item.fileName })}
                        onClick={() => void removeImage(item)}
                        className="inline-flex items-center justify-center rounded border border-input bg-surface p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-50"
                      >
                        <TrashIcon className="size-5" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-4">
                <label htmlFor="entry-item" className="text-sm font-medium text-foreground">
                  {t('studio.referenceEntry.addItem')}
                </label>
                <input
                  id="entry-item"
                  type="file"
                  accept="image/*"
                  className={inputClass}
                  disabled={busy}
                  onChange={(event) => void addImage(event.target.files?.[0] ?? null)}
                />
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}
